import { Fragment, useEffect, useRef, useState, useMemo } from 'react';
import {
	Button,
	Checkbox,
	Form,
	InputNumber,
	Modal,
	notification,
	Radio,
} from 'antd';
import { CloseCircleFilled, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { MaterialObject } from '../../../interfaces/Material.Interface.ts';
import { SignatureDefault } from '../../../components';
import {
	useGetUsersQuery,
	UserQueryInterface,
} from '../../../redux/api/users.ts';
import OperatorPinField from '../../../components/Operators/OperatorPinField';
import {
	useAddMaterialReceivedMutation,
	usePlanShortBarReceiveMutation,
} from '../../../redux/api/material-received.ts';
import { useIdempotencyKey } from '../../../utils/idempotency';
import MaterialLocationsSummaryModal from '../../../components/Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal';
import {
	getBundleMinLength,
	getMaterialReceivingBuildingId,
	isBundleMaterial,
} from './receivingBuilding.ts';
import {
	attachKeptJobs,
	hasUnplannedShortBars,
	buildOverReceiptMessage,
	buildReceivedBarsPayload,
	getPerBarExcess,
	isShortBar,
	normalizeShortPlanBars,
	receiveErrorMessage,
	shortPlanHasEmptyBar,
	shortPlanSelectionOverflow,
	ShortPlanBar,
	getUnreservedBarGroups,
	remainingOrderedQuantity,
	requiredKey,
	validateReceivedBars,
} from './receivingBars.ts';
import { usePrintLocation } from '../../../context/PrintLocationContext';
import { useGetBuildingsQuery } from '../../../redux/api/location.ts';

interface ReceivingFormProps {
	refetchData: () => void;
	material: MaterialObject;
	onCancel: (value: boolean) => void;
	building_id?: string;
}

export const ReceivingForm = (props: ReceivingFormProps) => {
	const userQuery: UserQueryInterface = {
		groups__name: 'Material Receivers',
	};

	const { data } = useGetUsersQuery(userQuery);
	const { data: buildings } = useGetBuildingsQuery();
	const materialBuildingId = getMaterialReceivingBuildingId(
		props.material,
		props.building_id
	);

	// Per-building receiving policy. The bundle threshold and whether short material
	// is auto-classified into racks (vs. sent to overflow) are configured on the
	// building, so a West Bend receive behaves differently from Cedarburg.
	const receivingBuilding = buildings?.find((b) => b.id === materialBuildingId);
	const bundleMinLength = getBundleMinLength(receivingBuilding);
	const autoAssignRacks = receivingBuilding?.auto_assign_racks ?? false;

	const [isLoading, setIsLoading] = useState(false);

	const [addMaterial, result] = useAddMaterialReceivedMutation();
	const [planShortBar] = usePlanShortBarReceiveMutation();
	const [shortPlan, setShortPlan] = useState<{
		values: any;
		bars: ShortPlanBar[];
	} | null>(null);
	// Keep a stable idempotency key across retries of the same receive (so a
	// request whose response is lost to a proxy timeout can be safely retried
	// without receiving the batch twice) and rotate it only after a confirmed
	// success. PTB-5938.
	const { getKey, rotate } = useIdempotencyKey();
	const { promptForBuilding } = usePrintLocation();

	const [qtyDelivered, setQtyDelivered] = useState(
		remainingOrderedQuantity(
			props.material.quantity_ordered,
			props.material.quantity_total_received
		)
	);
	// const [checkedMaterialRejected, setCheckedMaterialRejected] = useState(false);
	const [signature, setSignature] = useState<string | null>(null);

	const [maxQtyDelivered, setMaxQtyDelivered] = useState(
		remainingOrderedQuantity(
			props.material.quantity_ordered,
			props.material.quantity_total_received
		)
	);
	const [actualMaterialLength, setActualMaterialLength] = useState<
		number | null
	>(props.material.length ? parseFloat(props.material.length) : null);

	// Use actual material length (when set) for storage routing; fall back to ordered length for initial display.
	// The bundle threshold is per-building (West Bend = 48", others = 96").
	const isLongMaterial = isBundleMaterial(
		actualMaterialLength ?? (props.material.length ? parseFloat(props.material.length) : null),
		bundleMinLength
	);

	// Compute the minimum required length from unreserved breakdown bars for this material
	const minRequiredLength = useMemo(() => {
		const breakdown = props.material.material_order?.material_request_breakdown || [];
		const vendorPartNumber = props.material.vendor_part_number;
		const unreservedBars = breakdown.filter(
			(bar) => !bar.reserved && bar.vendor_part_number === vendorPartNumber && bar.total_length_used > 0
		);
		if (unreservedBars.length === 0) return 0;
		return Math.min(...unreservedBars.map((bar) => bar.total_length_used));
	}, [props.material]);

	const isActualLengthBelowRequired =
		actualMaterialLength !== null &&
		minRequiredLength > 0 &&
		actualMaterialLength < minRequiredLength;

	// PTB-5351 per-bar receiving: one row per distinct unreserved plan-bar
	// requirement, each validated against its own total_length_used. Scoped to
	// this line item's own stock length (PTB-6453) so a multi-length order's
	// sibling rows don't offer each other's bars.
	const barGroups = useMemo(
		() =>
			getUnreservedBarGroups(
				props.material.material_order?.material_request_breakdown || [],
				props.material.vendor_part_number || '',
				props.material.length ? parseFloat(props.material.length) : null
			),
		[props.material]
	);
	const usePerBar = barGroups.length > 0;
	const [actualByRequired, setActualByRequired] = useState<
		Record<string, number>
	>({});
	const [quantityByRequired, setQuantityByRequired] = useState<
		Record<string, number>
	>({});
	// Only bars the operator is actually receiving (qty > 0) need a valid length —
	// this keeps partial deliveries working.
	const activeBarGroups = usePerBar
		? barGroups.filter(
				(g) =>
					(quantityByRequired[requiredKey(g.requiredLength)] ??
						g.quantity) > 0
		  )
		: [];
	const perBarErrors = usePerBar
		? validateReceivedBars(activeBarGroups, actualByRequired)
		: [];

	// Seed each group's actual length to that group's own plan stock length
	// (PTB-6452 — a 61" remainder bar must default to 61, not the order's 144;
	// fallbacks: ordered length, then the requirement) and quantity to the full
	// group, when opening a material, without clobbering operator edits on
	// parent refetch (keyed on material id).
	useEffect(() => {
		if (!usePerBar) return;
		const orderedLength = props.material.length
			? parseFloat(props.material.length)
			: null;
		const initialLength: Record<string, number> = {};
		const initialQty: Record<string, number> = {};
		for (const g of barGroups) {
			const key = requiredKey(g.requiredLength);
			initialLength[key] =
				g.barLength || orderedLength || g.requiredLength;
			initialQty[key] = g.quantity;
		}
		setActualByRequired(initialLength);
		setQuantityByRequired(initialQty);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.material.id]);

	const [form] = Form.useForm();
	// PTB-6441: the static Modal.confirm() renders outside the
	// ConfigProvider/StyleProvider tree, so Tailwind's preflight strips the
	// primary-button styles and the confirm button is invisible until hovered
	// (same failure mode as the Modal.error note below). The hook instance
	// renders through contextHolder inside the app tree and inherits the theme.
	const [confirmModal, confirmContextHolder] = Modal.useModal();
	const prevMaterialIdRef = useRef<string | null>(null);

	// Watch form field so Receive Cost updates when user types in Actual Material Length
	const actualLengthFromForm = Form.useWatch('actual_material_length', form);
	const receivedBy = Form.useWatch('received_by', form);

	const materialCost = useMemo(() => {
		const unitPrice = parseFloat(props.material.unit_price || '0');
		const quantityOrdered = props.material.quantity_ordered || 0;
		return unitPrice * quantityOrdered;
	}, [props.material.unit_price, props.material.quantity_ordered]);

	// Use form value first so Receive Cost updates as user types; fall back to state then ordered length
	const effectiveActualLength =
		actualLengthFromForm ?? actualMaterialLength ?? (props.material.length ? parseFloat(props.material.length) : null);

	const receiveCost = useMemo(() => {
		if (qtyDelivered === 0 || props.material.quantity_ordered === 0) {
			return 0;
		}
		const baseCost =
			materialCost * (qtyDelivered / props.material.quantity_ordered);

		const orderedLength = parseFloat(props.material.length || '0');
		if (orderedLength > 0 && effectiveActualLength != null && effectiveActualLength !== undefined) {
			const lengthRatio = Number(effectiveActualLength) / orderedLength;
			return baseCost * lengthRatio;
		}

		return baseCost;
	}, [
		materialCost,
		qtyDelivered,
		props.material.quantity_ordered,
		effectiveActualLength,
		props.material.length,
	]);

	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [showLocationSummary, setShowLocationSummary] = useState(false);
	const [locationSummaryData, setLocationSummaryData] = useState<{
		materialInfo: any;
		lengths: any[];
	}>({
		materialInfo: {},
		lengths: [],
	});

	const submitReceive = (payload: any) =>
		promptForBuilding(
			async (selectedBuildingId) => {
				setIsLoading(true);
				try {
					await addMaterial({
						...payload,
						print_building_id: selectedBuildingId || undefined,
						idempotencyKey: getKey(),
					}).unwrap();
				} catch (e: any) {
					setErrorMessage(receiveErrorMessage(e));
				} finally {
					setIsLoading(false);
				}
			},
			{
				title: 'Where do you want to print the received tag?',
				okText: 'Submit & Print',
			},
		);

	const continueReceive = (payload: any) => {
		const overReceiptBy = payload.qty_delivered - maxQtyDelivered;
		const perBarExcess = usePerBar
			? getPerBarExcess(barGroups, quantityByRequired)
			: 0;
		if (overReceiptBy > 0 || perBarExcess > 0) {
			confirmModal.confirm({
				title: 'Receive more than planned?',
				content: buildOverReceiptMessage({
					qtyDelivered: payload.qty_delivered,
					remainingOrdered: maxQtyDelivered,
					perBarExcess,
				}),
				okText: 'Yes, receive it',
				onOk: () => submitReceive(payload),
			});
			return;
		}
		submitReceive(payload);
	};

	const handleFinish = async (values: any) => {
		if (values.method_type !== undefined && values.method_type !== null) {
			values.is_bundle = true;
		}

		if (usePerBar) {
			// PTB-5351 per-bar receiving: validate each received bar against its
			// plan-bar requirement, then send one received_bars row per group.
			if (activeBarGroups.length === 0) {
				notification.error({
					message: 'Validation Error',
					description: 'Enter a quantity for at least one bar to receive.',
					placement: 'topRight',
				});
				return;
			}
			const errors = validateReceivedBars(activeBarGroups, actualByRequired);
			if (errors.length) {
				notification.error({
					message: 'Validation Error',
					description: errors[0],
					placement: 'topRight',
				});
				return;
			}
			values.received_bars = buildReceivedBarsPayload(
				barGroups,
				actualByRequired,
				quantityByRequired
			);
			values.qty_delivered = values.received_bars.reduce(
				(sum: number, r: { quantity: number }) => sum + r.quantity,
				0
			);
			const actuals = values.received_bars
				.map((r: { actual_material_length: number }) => r.actual_material_length)
				.filter((v: number) => v != null && !Number.isNaN(v));
			values.actual_material_length = actuals.length
				? Math.max(...actuals)
				: null;
		} else {
			if (isActualLengthBelowRequired) {
				notification.error({
					message: 'Validation Error',
					description: `Actual length must be at least ${minRequiredLength.toFixed(3)}" to fulfill existing reservations.`,
					placement: 'topRight',
				});
				return;
			}

			if (values.actual_material_length < props.material.length) {
				notification.warning({
					message: 'Material Length Warning',
					description: 'The actual material length is shorter than the ordered length.',
					placement: 'topRight',
				});
			}

			if ('qty_delivered' in values) {
				values.qty_delivered = qtyDelivered;
			}

			// Always set actual_material_length, using fallback if not provided
			values.actual_material_length =
				actualMaterialLength !== null && actualMaterialLength !== undefined
					? actualMaterialLength
					: props.material.length
						? parseFloat(props.material.length)
						: null;
		}

		if (values.qty_delivered === 0 || signature === null || signature === '') {
			return;
		}

		values.signature = signature;
		values.material_order_line_item = props.material.id;
		values.building_id = materialBuildingId;
		// Added is_bundle to values to identify if the material received is part of a bundle

		if ('qty_rejected' in values) {
			if (values.qty_rejected > values.qty_delivered) {
				notification.error({
					message:
						'Rejected Quantity cannot be greater than Delivered Quantity',
					placement: 'topRight',
				});
				return;
			}
		}

		// A bar at the planned length is received on its own. A shorter bar asks
		// which jobs to keep (later scheduler jobs start unchecked) before the
		// same submit continues. PTB-7036.
		if (
			usePerBar &&
			(values.received_bars || []).some(isShortBar)
		) {
			setIsLoading(true);
			try {
				const plan = await planShortBar({
					material_order_line_item: props.material.id,
					received_bars: values.received_bars,
				}).unwrap();
				const bars = normalizeShortPlanBars(plan?.bars || []);
				// No plan rows left for these short lengths means every short
				// bar is over-receipt — book them without the keep/drop modal.
				if (!bars.length) {
					continueReceive(values);
					return;
				}
				setShortPlan({ values, bars });
				return;
			} catch (e: any) {
				notification.error({
					message: 'Could not plan the short bar',
					description:
						e?.data?.detail ||
						e?.error ||
						'The jobs on this bar could not be loaded.',
					placement: 'topRight',
				});
				return;
			} finally {
				setIsLoading(false);
			}
		}

		continueReceive(values);
	};

	useEffect(() => {
		if (result.isSuccess) {
			// Success — the next distinct receive should use a fresh key.
			rotate();
			notification.success({
				message: 'Material Received Successfully',
				placement: 'topRight',
			});
			props.refetchData();

			const responseData = result.data;

			const summaryData = {
				materialInfo: {
					type: responseData.material?.type_display_name || 'N/A',
					width: responseData.material?.width
						? Number(responseData.material.width)
						: undefined,
					height: responseData.material?.height
						? Number(responseData.material.height)
						: undefined,
					outer_diameter: responseData.material?.outer_diameter
						? Number(responseData.material.outer_diameter)
						: undefined,
					inner_diameter: responseData.material?.inner_diameter
						? Number(responseData.material.inner_diameter)
						: undefined,
					wall_thickness: responseData.material?.wall_thickness
						? Number(responseData.material.wall_thickness)
						: undefined,
					shape: responseData.material?.shape_display_name,
				},
				lengths:
					responseData.material_lengths?.map((length: any) => ({
						id: length.id,
						friendlyId: length.friendly_id,
						location: length.inventory_location?.display_name ?? length.inventory_location_display_name ?? length.material_shelf_display_name ?? 'Unknown',
						length: Number(length.current_length),
						isScrapped: length.inventory_location_display_name === 'SCRAP',
						overflowMessage: length.overflow_message || undefined,
					})) || [],
			};

			setLocationSummaryData(summaryData);
			setShowLocationSummary(true);
		}
	}, [result]);

	// Surface backend rejections (e.g. "Swiss not available at West Bend") in a
	// controlled modal. submitReceive unwraps the mutation and sets this too;
	// the effect covers any path that only leaves the error on the hook result.
	// A controlled <Modal> is used instead of Modal.error() because the static
	// API renders outside the ConfigProvider/StyleProvider tree and Tailwind's
	// preflight strips the button styles, leaving the OK button invisible.
	useEffect(() => {
		if (result.isError) {
			setErrorMessage(receiveErrorMessage(result.error));
		}
	}, [result.isError, result.error]);

	// Sync from props only when opening for a (possibly different) material, so we
	// don't overwrite user input if parent refetches and passes a new object reference.
	useEffect(() => {
		const materialId = props.material.id;
		const isNewMaterial = prevMaterialIdRef.current !== materialId;
		prevMaterialIdRef.current = materialId;

		const newQty =
			remainingOrderedQuantity(
			props.material.quantity_ordered,
			props.material.quantity_total_received
		);
		setMaxQtyDelivered(newQty);

		if (isNewMaterial) {
			setQtyDelivered(newQty);
			form.setFieldValue('qty_delivered', newQty);
			if (props.material.length) {
				const lengthValue = parseFloat(props.material.length);
				setActualMaterialLength(lengthValue);
				form.setFieldValue('actual_material_length', lengthValue);
			}
		}
	}, [props.material, form]);

	const handleCancel = () => {
		form.resetFields();
		// setCheckedMaterialRejected(false);
		// Reset to initial values so if parent keeps this instance mounted, state is valid when reopened
		const initialQty =
			remainingOrderedQuantity(
			props.material.quantity_ordered,
			props.material.quantity_total_received
		);
		setQtyDelivered(initialQty);
		setMaxQtyDelivered(initialQty);
		setActualMaterialLength(
			props.material.length ? parseFloat(props.material.length) : null
		);
		form.setFieldValue('qty_delivered', initialQty);
		if (props.material.length) {
			form.setFieldValue(
				'actual_material_length',
				parseFloat(props.material.length)
			);
		}
		setSignature(null);
		prevMaterialIdRef.current = null; // next open will sync from props again
		props.onCancel(true);
	};

	useEffect(() => {
		if (isLongMaterial) {
			form.setFieldsValue({ is_bundle: true });
		} else {
			// Clear method_type when switching to Overflow so we don't submit a stale FastLane/Swiss choice
			form.setFieldsValue({ method_type: undefined });
		}
	}, [isLongMaterial, form]);

	const isSubmitDisabled = () =>
		signature === null ||
		!receivedBy ||
		(usePerBar
			? activeBarGroups.length === 0 || perBarErrors.length > 0
			: qtyDelivered === 0 ||
			  form.getFieldValue('actual_material_length') == null ||
			  isActualLengthBelowRequired);

	return (
		<Fragment>
			{confirmContextHolder}
			<Form layout="vertical" form={form} onFinish={handleFinish}>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					<div className="p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">Vendor Order Number</div>
							<div>{props.material.material_order.vendor_order_number}</div>
						</div>
					</div>

					<div className="p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">PO Number</div>
							<div>{props.material.material_order.purchase_order_number}</div>
						</div>
					</div>

					<div className="p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">Material Type</div>
							<div>{props.material.type.display_name}</div>
						</div>
					</div>

					<div className="p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">Material Shape</div>
							<div>{props.material.shape.display_name}</div>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
					<div className="p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">Material Size</div>
						</div>
						<span>{props.material.display_size}</span>
					</div>

					<div className="p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">Material Length</div>
							<div>{props.material.length} In.</div>
						</div>
					</div>

					<div className="p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">Ordered / Delivered</div>
							<div>
								{props.material.quantity_ordered} /{' '}
								{props.material.quantity_total_received}
							</div>
						</div>
					</div>

					<div className="p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">Material Cost</div>
							<div>${materialCost.toFixed(2)}</div>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div className={usePerBar ? 'md:col-span-2 w-full' : 'w-full'}>
						{usePerBar ? (
							<Form.Item label="Actual Material Length (per bar)" required>
								{/* PTB-6451: one shared grid so the label / quantity / × /
								    length / unit cells align in columns across rows (a
								    per-row flex let the variable-width "Needs ≥" label
								    shift every column). Rows use display:contents, so the
								    five cells of each row land in the five tracks.
								    PTB-6469: the hint is NOT a sixth track — as a
								    `1fr` column it could not shrink below the sentence's
								    min-content width and pushed the grid into a horizontal
								    scroll. It spans the full row on its own line instead,
								    so it wraps freely and stays inside the modal. Rows
								    without a hint simply emit no sixth cell, which keeps
								    the five columns aligned. */}
								<div className="overflow-x-auto">
									<div
										className="grid items-center gap-x-2 gap-y-1"
										style={{
											gridTemplateColumns: 'repeat(5, max-content)',
										}}
									>
										{barGroups.map((g) => {
											const key = requiredKey(g.requiredLength);
											const actual = actualByRequired[key];
											const qty = quantityByRequired[key] ?? g.quantity;
											const below =
												qty > 0 &&
												actual != null &&
												!Number.isNaN(actual) &&
												actual + 0.01 < g.requiredLength;
											return (
												<div key={key} className="contents">
													<span className="text-sm whitespace-nowrap">
														Needs ≥ {g.requiredLength.toFixed(3)}&quot;
													</span>
													<InputNumber
														aria-label={`quantity for ${g.requiredLength}in bars`}
														min={0}
														precision={0}
														value={qty}
														onChange={(value: number | null) =>
															setQuantityByRequired((prev) => ({
																...prev,
																[key]: (value as number) ?? 0,
															}))
														}
													/>
													<span className="text-sm">×</span>
													<InputNumber
														aria-label={`actual length for ${g.requiredLength}in bars`}
														style={{ minWidth: 120 }}
														min={0}
														max={300}
														step={0.001}
														precision={3}
														status={below ? 'warning' : undefined}
														disabled={qty === 0}
														value={actual}
														onChange={(value: number | null) =>
															setActualByRequired((prev) => ({
																...prev,
																[key]: value as number,
															}))
														}
													/>
													<span className="text-sm">In.</span>
													{below ? (
														<span
															className="text-yellow-600 text-xs"
															style={{ gridColumn: '1 / -1' }}
														>
															Shorter than the {g.requiredLength.toFixed(3)}&quot; planned. You&apos;ll choose which jobs to keep.
														</span>
													) : qty > g.quantity ? (
														<span
															className="text-yellow-600 text-xs"
															style={{ gridColumn: '1 / -1' }}
														>
															+{qty - g.quantity} more than planned (received
															unreserved)
														</span>
													) : null}
												</div>
											);
										})}
									</div>
								</div>
							</Form.Item>
						) : (
							<Form.Item
								name="actual_material_length"
								label="Actual Material Length"
								initialValue={
									props.material.length ? parseFloat(props.material.length) : null
								}
								rules={[
									{
										required: true,
										message: 'Actual Material Length is required',
									},
									{
										type: 'number',
										min: 0,
										max: 300,
										message:
											'Actual Material Length must be between 0.000 and 300.000',
									},
								]}
							>
								<InputNumber
									style={{ width: '100%' }}
									min={0}
									max={300}
									step={0.001}
									precision={3}
									placeholder={`${props.material.length} In.`}
									value={actualMaterialLength}
									onChange={(value: number | null) => {
										setActualMaterialLength(value);
										form.setFieldValue('actual_material_length', value);
									}}
								/>
								{isActualLengthBelowRequired ? (
									<div className="text-red-500">
										Actual length must be at least {minRequiredLength.toFixed(3)}&quot; to fulfill existing reservations.
									</div>
								) : form.getFieldValue('actual_material_length') < props.material.length ? (
									<div className="text-yellow-600">
										This value is less than the ordered length ({props.material.length} In.)
									</div>
								) : null}
							</Form.Item>
						)}
					</div>

					{!usePerBar && (
					<div className="w-full">
						<Form.Item
							name="qty_delivered"
							label="Received Quantity"
							initialValue={qtyDelivered}
							rules={[
								{
									required: true,
								},
							]}
						>
							<div className="receive-material-qty-controls">
								<Button
									type="default"
									icon={<MinusOutlined />}
									onClick={() => {
										if (qtyDelivered > 0) {
											const next = qtyDelivered - 1;
											setQtyDelivered(next);
											form.setFieldValue('qty_delivered', next);
										}
									}}
								/>

								<InputNumber
									controls={false}
									min={1}
									value={qtyDelivered}
									onChange={(value: number | null): void => {
										if (value !== null && value !== undefined) {
											const integerQtyDelivered: boolean =
												Number.isInteger(value);
											const nextValue = integerQtyDelivered
												? value
												: Math.ceil(value);
											if (!integerQtyDelivered) {
												notification.info({
													message: `Delivered Quantity: ${value} was rounded to ${Math.ceil(
														value
													)}`,
													placement: 'topRight',
												});
											}
											setQtyDelivered(nextValue);
											form.setFieldValue('qty_delivered', nextValue);
										}
									}}
								/>

								<Button
									type="default"
									icon={<PlusOutlined />}
									onClick={() => {
										const next = qtyDelivered + 1;
										setQtyDelivered(next);
										form.setFieldValue('qty_delivered', next);
									}}
								/>

								{qtyDelivered === 0 ? (
									<div className="text-red-500">Please enter delivered qty</div>
								) : qtyDelivered > maxQtyDelivered ? (
									<div className="text-yellow-600">
										Exceeds the remaining ordered quantity ({maxQtyDelivered}{' '}
										remaining)
									</div>
								) : null}
							</div>
						</Form.Item>
					</div>
					)}

					<div className="w-full p-2">
						<div className="flex flex-col justify-between">
							<div className="font-light">Received Cost</div>
							<div>${receiveCost.toFixed(2)}</div>
						</div>
					</div>
				</div>

				<Form.Item
					name={'packing_slip'}
					initialValue={true}
					valuePropName="checked"
				>
					<Checkbox>The delivered quantity matches the Packaging Slip</Checkbox>
				</Form.Item>

				{/*<Form.Item*/}
				{/*	name={'material_rejected'}*/}
				{/*	initialValue={false}*/}
				{/*	valuePropName="checked"*/}
				{/*>*/}
				{/*	<Checkbox onChange={handleChangeMaterialRejected}>*/}
				{/*		Material Rejected*/}
				{/*	</Checkbox>*/}
				{/*</Form.Item>*/}

				{isLongMaterial ? (
					<div>
						<Checkbox checked={true}>
							The delivered quantity of the material is a Bundle
						</Checkbox>
					</div>
				) : autoAssignRacks ? (
					<div>
						This material will be auto-assigned to the matching rack (bin, slot or
						shelf) based on its size because its length is under {bundleMinLength}{' '}
						inches
					</div>
				) : (
					<div>
						This material will be stored in Overflow because its length is under{' '}
						{bundleMinLength} inches
					</div>
				)}

				{isLongMaterial && (
					<div style={{ marginLeft: 26, marginTop: 12 }}>
						<Form.Item
							label="Select how the Bundle will be assigned in inventory"
							name="method_type"
							style={{ marginBottom: 0 }}
							rules={[{ required: true, message: 'Please select a method' }]}
						>
							<Radio.Group>
								<Radio value="FastLane">FastLane</Radio>
								<Radio value="Swiss">Swiss</Radio>
							</Radio.Group>
						</Form.Item>
					</div>
				)}

				<div style={{ marginTop: '16px', marginBottom: '24px', width: '100%' }}>
					<OperatorPinField
						label="Received By"
						userData={data?.results || []}
						expectedGroups={['Material Receivers']}
						value={receivedBy}
						onResolved={(userId) =>
							form.setFieldsValue({ received_by: userId })
						}
					/>
				</div>
				<Form.Item name="received_by" hidden>
					<InputNumber />
				</Form.Item>

				<SignatureDefault
					signature={(value: string) => {
						if (value !== '') {
							setSignature(value);
						} else {
							setSignature(null);
						}
					}}
					showSignature={false}
					clearSignaturePad={signature === null}
				/>
				{signature === null ? (
					<div className="text-red-500">Please sign to submit</div>
				) : null}

				<div className={'flex flex-row justify-end mt-4'}>
					<Button type={'default'} onClick={handleCancel} className={'mx-2'}>
						Cancel
					</Button>
					<Button
						type="primary"
						htmlType="submit"
						loading={isLoading}
						disabled={isSubmitDisabled()}
					>
						Submit
					</Button>
				</div>
			</Form>
			<Modal
				open={shortPlan !== null}
				title="This bar is shorter than planned"
				okText="Receive and reorder the rest"
				okButtonProps={{
					disabled:
						!shortPlan ||
						shortPlanSelectionOverflow(shortPlan.bars) ||
						shortPlanHasEmptyBar(shortPlan.bars),
				}}
				onOk={() => {
					if (!shortPlan || shortPlanHasEmptyBar(shortPlan.bars)) return;
					const receivedBars = attachKeptJobs(
						shortPlan.values.received_bars,
						shortPlan.bars
					);
					if (hasUnplannedShortBars(receivedBars, shortPlan.bars)) {
						notification.error({
							message: 'Could not plan the short bar',
							description:
								'One or more short bars could not be matched to planned jobs, so the receive cannot continue.',
							placement: 'topRight',
						});
						return;
					}
					const payload = {
						...shortPlan.values,
						received_bars: receivedBars,
					};
					setShortPlan(null);
					continueReceive(payload);
				}}
				onCancel={() => setShortPlan(null)}
				maskClosable={false}
				centered
				width={640}
			>
				<p>
					Checked jobs stay reserved on the bar that arrived. Later jobs in
					the scheduler start unchecked so there is time to order more. Leave
					a job unchecked to request that material again.
				</p>
				{shortPlan?.bars.map((bar, barIndex) => {
					const keptInches = bar.jobs
						.filter((job) => job.keep)
						.reduce((sum, job) => sum + job.inches, 0);
					const droppedInches = bar.jobs
						.filter((job) => !job.keep)
						.reduce((sum, job) => sum + job.inches, 0);
					const overflow = keptInches > bar.actual_material_length + 0.01;
					return (
						<div key={`${bar.plan_index}-${barIndex}`} className="mb-4">
							<div className="font-medium">
								{bar.actual_material_length.toFixed(3)}&quot; received,{' '}
								{bar.required_length.toFixed(3)}&quot; planned
							</div>
							{bar.jobs.length === 0 ? (
								<div className="text-red-500 text-xs">
									This bar has no jobs to keep, so it cannot be received
									short.
								</div>
							) : null}
							{bar.jobs.map((job, jobIndex) => (
								<div key={`${job.order_line_item_id}-${jobIndex}`}>
									<Checkbox
										checked={job.keep}
										onChange={(event) => {
											const checked = event.target.checked;
											setShortPlan((current) => {
												if (!current) return current;
												return {
													...current,
													bars: current.bars.map((item, index) =>
														index === barIndex
															? {
																	...item,
																	jobs: item.jobs.map(
																		(candidate, candidateIndex) =>
																			candidateIndex === jobIndex
																				? {
																						...candidate,
																						keep: checked,
																					}
																				: candidate
																	),
																}
															: item
													),
												};
											});
										}}
									>
										{job.name} ({job.inches.toFixed(2)}&quot;)
										{job.scheduled_start
											? ''
											: ' (not on the scheduler)'}
									</Checkbox>
								</div>
							))}
							{bar.jobs.length === 0 ? null : (
								<div className={overflow ? 'text-red-500 text-xs' : 'text-xs'}>
									{keptInches.toFixed(2)}&quot; kept of{' '}
									{bar.actual_material_length.toFixed(2)}&quot;. {droppedInches.toFixed(2)}
									&quot; will be requested again.
									{overflow
										? ' The checked jobs do not fit on this bar.'
										: ''}
								</div>
							)}
						</div>
					);
				})}
			</Modal>
			<Modal
				open={errorMessage !== null}
				title={
					<span>
						<CloseCircleFilled style={{ color: '#ff4d4f', marginRight: 8 }} />
						Cannot receive material
					</span>
				}
				onOk={() => setErrorMessage(null)}
				onCancel={() => setErrorMessage(null)}
				okText="OK"
				cancelButtonProps={{ style: { display: 'none' } }}
				maskClosable={false}
				centered
			>
				{errorMessage}
			</Modal>
			{showLocationSummary && (
				<MaterialLocationsSummaryModal
					visible={showLocationSummary}
					onComplete={() => {
						setShowLocationSummary(false);
						handleCancel();
					}}
					materialInfo={locationSummaryData.materialInfo}
					lengths={locationSummaryData.lengths}
					title="Material Locations Summary"
					source="receiving"
				/>
			)}
		</Fragment>
	);
};
